import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import html2canvas from "html2canvas";
import { useTranslation } from "hooks";
import { saveAs } from "file-saver";
import { Button, Dialog, Icon, Spinner } from "components";
import { Alisketch } from "@stemscopes/part-types";
import { ImageObject } from "@stemscopes/part-types/src/types";
import { types, utils } from "./duck";
import styles from "./Screenshot.module.css";

const Screenshot: React.FC<types.ScreenshotProps> = ({
  opened,
  onCloseCallback,
  uploadCallback,
}) => {
  const screenshotRef = useRef<ImageObject | undefined>(undefined);
  const closureAvailabilityRef = useRef<boolean>(true);
  const [isScreenshotStored, setIsScreenshotStored] = useState<boolean>(false);

  const sketchWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isSketchWrapperRefInitialized, setIsSketchWrapperRefInitialized] =
    useState<boolean>(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (opened) {
      html2canvas(document.body, {
        useCORS: true,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scale: 1,
      }).then((el) => {
        screenshotRef.current = {
          original: el.toDataURL(),
        };
        setIsScreenshotStored(true);
      });
    }

    if (!opened) {
      screenshotRef.current = undefined;
      sketchWrapperRef.current = null;
      setIsScreenshotStored(false);
      setIsSketchWrapperRefInitialized(false);
    }
  }, [opened]);

  const getCanvas: types.getCanvas = () =>
    sketchWrapperRef.current?.querySelector("canvas");

  const handleUpload: types.handleUpload = () => {
    const canvas = getCanvas();
    if (canvas) {
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], utils.getScreenshotDefaultName());
          uploadCallback(file);
        }
      });
    }
  };

  const handleDownLoad: types.handleDownload = () => {
    const canvas = getCanvas();
    if (canvas) {
      canvas.toBlob((blob) => {
        if (blob) {
          saveAs(blob, utils.getScreenshotDefaultName());
        }
      });
    }
  };

  if (opened && !isScreenshotStored) {
    return ReactDOM.createPortal(
      <div data-html2canvas-ignore="true">
        <Spinner size="large" className={styles.spinner} />
      </div>,
      document.body,
    );
  }

  return (
    <>
      <Dialog
        title={t("feedback.screenshot")}
        opened={opened}
        onClose={() => {
          if (closureAvailabilityRef?.current) {
            onCloseCallback();
          }
        }}
        className={styles.modal}
      >
        <div
          ref={(ref) => {
            sketchWrapperRef.current = ref;
            setIsSketchWrapperRefInitialized(true);
          }}
          className={styles.aliContainer}
        >
          {isSketchWrapperRefInitialized &&
            sketchWrapperRef.current?.offsetWidth && (
              <Alisketch
                closureAvailabilityRef={closureAvailabilityRef}
                backgroundImage={screenshotRef?.current}
                canvasWidth={`${sketchWrapperRef.current.offsetWidth}px`}
                canvasHeight={`${
                  (window.innerHeight * sketchWrapperRef.current.offsetWidth) /
                  window.innerWidth
                }px`}
                hasCrop
              />
            )}
        </div>
        <div className={styles.controls}>
          <Button
            variant="clear"
            prefix={<Icon name="download-outlined" />}
            className={styles.controlDownload}
            onClick={handleDownLoad}
          >
            {t("feedback.download")}
          </Button>
          <Button onClick={handleUpload}>{t("feedback.upload")}</Button>
        </div>
      </Dialog>
    </>
  );
};

export default Screenshot;
